import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { z } from "zod";
//#region src/user-passkeys.ts
/**
* `/v1/admin/users/{id}/passkeys`: the admin passkey reset of one member (kernel apps, WS-2b,
* `design-docs/WS-2.md` section 3.6, R-054).
*
* Producer: core-api's second-factor module (WS-10a) in `cloud/dp/core-api/src/auth/second-factor/`.
* Consumers: the kernel shell (WS-15) and the org admin app (EX-1). The route removes every passkey
* of one member of the caller's org. The member registers a new kernel passkey at the next login
* (I-9).
*
* Kernel class: `kernel`, right `identity.users`, tier 1 (R-054). `CONTRACT_ROUTE_KERNEL_CLASS` in
* `route-census.ts` holds that class, and this route sits in the kernel fold
* `KERNEL_CONTRACT_ROUTES`, apart from the 122 routes of `ADMIN_CONTRACT_ROUTES`.
*
* Direction rule (R-28): the response schema is `z.object`, so a member that core-api adds is
* stripped. The route takes no body. No schema here carries a `.transform()`, so
* `createOpenApiDocument()` can represent each one.
*/
/** The route path in OpenAPI `{param}` form. Fill it with {@link adminUserPasskeysPath}. */
const ADMIN_USER_PASSKEYS_PATH_TEMPLATE = `${ADMIN_API_PREFIX}/users/{id}/passkeys`;
/**
* `DELETE /v1/admin/users/{id}/passkeys`. Removes every passkey of the member `{id}` in the
* caller's org.
*
* - 200 with {@link resetUserPasskeysResponseSchema}: the member and the count of removed rows.
*   A member with no passkey answers 200 with `removed: 0`.
* - 404 for an id that names no member of the caller's org, so the route is no cross-tenant
*   existence oracle.
*
* Permission `users:manage`. Kernel class `kernel` `identity.users` tier 1 (R-054): the kernel
* guard (WS-19) asks a step-up before the call runs.
*/
const RESET_USER_PASSKEYS_ROUTE = route("DELETE", ADMIN_USER_PASSKEYS_PATH_TEMPLATE);
/**
* The 200 body of {@link RESET_USER_PASSKEYS_ROUTE}.
*
* - `userId`: the member, a uuid.
* - `removed`: the count of passkey rows that the call removed, an integer of 0 or more.
*
* A negative or fractional count fails the parse.
*/
const resetUserPasskeysResponseSchema = z.object({
	userId: z.uuid(),
	removed: z.int().min(0)
});
/**
* Fill {@link ADMIN_USER_PASSKEYS_PATH_TEMPLATE} with one member id.
*
* @param userId the member uuid.
* @returns the request path, for example `/v1/admin/users/<uuid>/passkeys`.
* @throws a `ZodError` when `userId` is not a uuid, so a slash, a dot-segment, a slug or an empty
*   string never reaches a URL. Pure.
*/
function adminUserPasskeysPath(userId) {
	return fillPath(ADMIN_USER_PASSKEYS_PATH_TEMPLATE, { id: userId }, { id: z.uuid() });
}
/** Every route this file declares, for the kernel fold of `route-census.ts`. */
const userPasskeysRoutes = Object.freeze([RESET_USER_PASSKEYS_ROUTE]);
//#endregion
export { ADMIN_USER_PASSKEYS_PATH_TEMPLATE, RESET_USER_PASSKEYS_ROUTE, adminUserPasskeysPath, resetUserPasskeysResponseSchema, userPasskeysRoutes };

//# sourceMappingURL=user-passkeys.js.map